import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import { Menu as MenuIcon, Brightness4, Brightness7, Clear } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';

interface HeaderProps {
  drawerOpen: boolean;
  onDrawerToggle: () => void;
  currentTournament: { name: string; date: string; id: string } | null;
  onThemeToggle?: () => void;
}

const Header: React.FC<HeaderProps> = ({ drawerOpen, onDrawerToggle, currentTournament, onThemeToggle }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { user, isAuthenticated, signOut } = useAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const handleLogin = () => {
    setLoginModalOpen(true);
  };

  const handleLogout = () => {
    signOut();
  };

  const handleLoginModalClose = () => {
    setLoginModalOpen(false);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClearLocalStorage = () => {
    try {
      console.log('Clearing all local storage data...');
      
      // Get all localStorage keys first before removing anything
      const keys = Object.keys(localStorage);
      console.log(`Found ${keys.length} localStorage items to clear:`, keys);
      
      // Clear all localStorage items comprehensively
      keys.forEach(key => {
        console.log(`Removing localStorage item: ${key}`);
        localStorage.removeItem(key);
      });
      
      // Double-check that localStorage is completely empty
      const remainingKeys = Object.keys(localStorage);
      if (remainingKeys.length > 0) {
        console.warn('Found remaining localStorage items after clearing:', remainingKeys);
        // Force clear any remaining items
        remainingKeys.forEach(key => {
          localStorage.removeItem(key);
        });
      }
      
      // Clear sessionStorage as well for complete reset
      sessionStorage.clear();
      
      console.log('All local storage and session storage data cleared successfully');
      
      // Reload the page to reset everything
      window.location.reload();
    } catch (error) {
      console.error('Error clearing local storage:', error);
    }
  };

  return (
    <>
      <AppBar position="fixed" sx={{ backgroundColor: '#1F2630', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <img 
              src="/LOGO.png" 
              alt="Tournament Logo" 
              style={{ height: 40, width: 'auto' }}
            />
            <Typography variant="h6" noWrap component="div" sx={{ color: '#FFFFFF' }}>
              Wrestling Tournament Manager {currentTournament && (<span> :: {currentTournament.name} ({currentTournament.date})</span>)}
            </Typography>
          </Box>

          {isAuthenticated ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                padding: '4px 8px', 
                borderRadius: '4px',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <Typography 
                  variant="body2" 
                  color="inherit" 
                  sx={{ 
                    fontWeight: 'medium',
                    fontSize: '0.875rem'
                  }}
                >
                  {user?.username || 'User'}
                </Typography>
              </Box>
              
              <IconButton
                color="inherit"
                aria-label="user menu"
                onClick={handleUserMenuOpen}
                sx={{ 
                  ml: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  padding: '4px',
                  borderRadius: '4px',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <MenuIcon />
              </IconButton>
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
              >
                <MenuItem onClick={handleUserMenuClose}>Profile</MenuItem>
                <MenuItem onClick={handleUserMenuClose}>Settings</MenuItem>
                <MenuItem onClick={() => {
                  handleLogout();
                  handleUserMenuClose();
                }}>Logout</MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button
              color="inherit"
              onClick={handleLogin}
              sx={{ 
                ml: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                padding: '4px 8px', 
                borderRadius: '4px',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              Login
            </Button>
          )}
        
          {/* Clear Local Storage Button */}
          <IconButton
            color="inherit"
            aria-label="clear local storage"
            onClick={handleClearLocalStorage}
            sx={{ 
              ml: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
              padding: '4px',
              borderRadius: '4px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
            title="Clear Local Storage"
          >
            <Clear />
          </IconButton>
        
          {/* Theme Toggle Button */}
          <IconButton
            color="inherit"
            aria-label="toggle theme"
            onClick={onThemeToggle}
            sx={{ 
              ml: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
              padding: '4px',
              borderRadius: '4px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            {isDarkMode ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <LoginModal open={loginModalOpen} onClose={handleLoginModalClose} />
    </>
  );
};

export default Header;
